import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InlineSpinner } from "@/components/common/Loading";
import { CalendarClock, ChevronLeft, CheckCircle2, User } from "lucide-react";
import { toast } from "sonner";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/group-schedule`;
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function PublicGroupBooking() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState(false);
  const [step, setStep] = useState(1);
  const [activity, setActivity] = useState(null);
  const [host, setHost] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile: "", interested_in: "", comments: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { axios.get(`${BASE}/${slug}`).then((r) => setPage(r.data)).catch(() => setErr(true)); }, [slug]);
  useEffect(() => {
    if (step !== 3 || !activity || !host) return;
    setLoadingSlots(true); setSlot(null);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    axios.get(`${BASE}/${slug}/slots`, { params: { appointment_type_id: activity.id, host_id: host.id, date, tz } })
      .then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([])).finally(() => setLoadingSlots(false));
  }, [step, activity, host, date, slug]);

  const book = async () => {
    for (const f of ["first_name", "last_name", "email", "mobile"]) if (!form[f].trim()) return toast.error("Please complete all required fields");
    const fr = activity?.field_rules || {};
    if (fr.interested_in === "required" && !form.interested_in) return toast.error("Please select an Interested In option");
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/${slug}/book`, { appointment_type_id: activity.id, host_id: host.id, start: slot.start, ...form });
      setDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not book. Please try again."); } finally { setBusy(false); }
  };

  if (err) return <Centered><p className="text-slate-500">This booking page is unavailable.</p></Centered>;
  if (!page) return <Centered><InlineSpinner /></Centered>;
  if (done) return (
    <Centered>
      <div className="text-center max-w-md" data-testid="group-book-confirmation">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
        <h1 className="font-display text-2xl font-bold text-slate-900">You're booked!</h1>
        <p className="text-slate-600 mt-2">{done.appointment_type} with {host.name}</p>
        <p className="text-slate-800 font-medium mt-1">{new Date(done.start).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
        {done.meeting_link ? (
          <a href={done.meeting_link} target="_blank" rel="noreferrer" data-testid="group-confirm-join"
            className="inline-block mt-4 bg-[#0F2B5B] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:opacity-90">Join Microsoft Teams Meeting</a>
        ) : done.location ? <p className="text-sm text-slate-600 mt-3">{done.location}</p> : null}
        <p className="text-xs text-slate-400 mt-4">A confirmation has been recorded. You may close this page.</p>
      </div>
    </Centered>
  );

  const steps = ["Activity", "Host", "Time", "Your details"];
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="public-group-booking">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          {page.branding?.logo_url && <img src={page.branding.logo_url} alt="" className="h-10 mx-auto mb-3" />}
          <h1 className="font-display text-2xl font-bold text-slate-900">{page.name}</h1>
          {page.intro && <p className="text-sm text-slate-500 mt-1">{page.intro}</p>}
        </div>
        <div className="flex items-center justify-center gap-2 mb-6">
          {steps.map((s, i) => (
            <div key={s} className={`flex items-center gap-1.5 text-xs font-medium ${step === i + 1 ? "text-[#006090]" : "text-slate-400"}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step > i + 1 ? "bg-emerald-500 text-white" : step === i + 1 ? "bg-[#006090] text-white" : "bg-slate-200"}`}>{i + 1}</span>
              <span className="hidden sm:inline">{s}</span>{i < steps.length - 1 && <span className="text-slate-300 mx-1">›</span>}
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          {step > 1 && <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-[#006090] mb-4" data-testid="group-book-back"><ChevronLeft className="w-4 h-4" />Back</button>}

          {step === 1 && (
            <div className="space-y-2" data-testid="group-step-activity">
              <p className="font-semibold text-slate-800 mb-2">Select an activity</p>
              {page.appointment_types.map((t) => (
                <button key={t.id} onClick={() => { setActivity(t); setHost(null); setStep(2); }} data-testid={`group-activity-pick-${t.id}`}
                  className="w-full text-left rounded-xl border border-slate-200 p-4 hover:border-[#006090] hover:shadow-sm transition-all flex items-center gap-3">
                  <CalendarClock className="w-5 h-5 text-[#006090]" />
                  <div><p className="font-medium text-slate-800">{t.name}</p><p className="text-xs text-slate-500">{t.duration_min} min{t.description ? ` · ${t.description}` : ""}</p></div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && activity && (
            <div className="space-y-2" data-testid="group-step-host">
              <p className="font-semibold text-slate-800 mb-2">Choose who you'll meet</p>
              {activity.hosts.map((h) => (
                <button key={h.id} onClick={() => { setHost(h); setStep(3); }} data-testid={`group-host-pick-${h.id}`}
                  className="w-full text-left rounded-xl border border-slate-200 p-4 hover:border-[#006090] hover:shadow-sm transition-all flex items-center gap-3">
                  {h.photo_url ? <img src={h.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full bg-[#006090]/10 text-[#006090] flex items-center justify-center"><User className="w-4 h-4" /></span>}
                  <p className="font-medium text-slate-800">{h.name}</p>
                </button>
              ))}
            </div>
          )}

          {step === 3 && (
            <div data-testid="group-step-time">
              <p className="font-semibold text-slate-800 mb-3">Pick a time with {host.name}</p>
              <Input type="date" value={date} min={todayStr()} onChange={(e) => setDate(e.target.value)} className="mb-3 max-w-[200px]" data-testid="group-book-date" />
              {loadingSlots ? <div className="py-8 flex justify-center"><InlineSpinner /></div>
                : slots.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No open times on this day. Try another date.</p>
                : <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {slots.map((s) => (
                      <button key={s.start} onClick={() => { setSlot(s); setStep(4); }} data-testid={`group-slot-${s.start}`}
                        className="rounded-lg border border-slate-200 py-2 text-sm text-slate-700 hover:border-[#006090] hover:text-[#006090]">{s.label}</button>
                    ))}
                  </div>}
            </div>
          )}

          {step === 4 && slot && (
            <div className="space-y-3" data-testid="group-step-form">
              <p className="font-semibold text-slate-800">Your details</p>
              <p className="text-xs text-slate-500">{activity.name} with {host.name} · {new Date(slot.start).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1" data-testid="group-book-first" /></div>
                <div><Label className="text-xs font-semibold">Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1" data-testid="group-book-last" /></div>
                <div><Label className="text-xs font-semibold">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="group-book-email" /></div>
                <div><Label className="text-xs font-semibold">Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="mt-1" data-testid="group-book-mobile" /></div>
              </div>
              {(activity?.field_rules?.interested_in || "optional") !== "hidden" && (
                <div><Label className="text-xs font-semibold">Interested In{(activity?.field_rules?.interested_in === "required") ? "*" : ""}</Label>
                  <Select value={form.interested_in} onValueChange={(v) => setForm({ ...form, interested_in: v })}>
                    <SelectTrigger className="mt-1" data-testid="group-book-interested"><SelectValue placeholder="Select an option" /></SelectTrigger>
                    <SelectContent className="bg-white">{(page.interest_options || []).map((o) => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label className="text-xs font-semibold">Anything to share? (optional)</Label><Textarea value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} rows={2} className="mt-1" data-testid="group-book-comments" /></div>
              <input type="text" value={form.company_website || ""} onChange={(e) => setForm({ ...form, company_website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" />
              <Button onClick={book} disabled={busy} className="w-full bg-[#006090] hover:bg-[#004E75]" data-testid="group-book-submit">{busy ? <InlineSpinner /> : "Confirm booking"}</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">{children}</div>;
}
